import { Fragment, useEffect, useRef, useState } from 'react';
import './SchoolProjects.css';

const schoolProjects = [
  {
    name: 'Zoidberg 2.0',
    school: 'Epitech',
    year: '2ᵉ année',
    color: 'blue',
    description:
      'Détection de maladies pulmonaires par IA (module T-DEV-810). Entraînement d\'un modèle de classification sur des radiographies thoraciques pour identifier une pneumonie virale ou bactérienne. Pipeline complet en Python : préparation du jeu d\'images, exploration et expérimentations en notebooks Jupyter, puis entraînement et évaluation du modèle.',
    tags: ['Python', 'Machine Learning', 'Jupyter', 'Computer Vision'],
    github: 'https://github.com/ErenVarli/Zoidberg2.0',
  },
  {
    name: 'Time Manager',
    school: 'Epitech',
    year: '2ᵉ année',
    color: 'blue',
    description:
      'Application web de gestion du temps de travail (module T-DEV-700), réalisée à 3 sur 3 mois. Pointage des heures côté employé, gestion des équipes et tableaux de bord KPI côté manager, avec trois rôles cloisonnés (admin, manager, employé). API REST Node/Express, MongoDB, JWT en cookie HttpOnly, le tout conteneurisé derrière un reverse proxy NGINX et construit à chaque push via GitHub Actions.',
    tags: ['React', 'Vite', 'TypeScript', 'Node.js', 'Express', 'MongoDB', 'JWT', 'Docker', 'NGINX', 'Jest'],
    github: 'https://github.com/ErenVarli/Time-Manager',
  },
  {
    name: 'NSA-501',
    school: 'Epitech',
    year: '1ʳᵉ année',
    color: 'blue',
    description:
      'Mise en place d\'une infrastructure réseau composée de quatre machines virtuelles (VMs) connectées via un réseau configuré avec des règles de sécurité strictes pour gérer et filtrer le trafic efficacement. Module DevOps NSA 501.',
    tags: ['Réseau', 'VMs', 'DevOps', 'Sécurité'],
    github: 'https://github.com/ErenVarli/NSA-501',
  },
  {
    name: 'JAV-501',
    school: 'Epitech',
    year: '1ʳᵉ année',
    color: 'blue',
    description:
      'Garfield 2D Game : jeu de plateforme 2D développé en Java, inspiré du célèbre Kirby. Le joueur incarne Garfield, un chat qui doit franchir des obstacles, affronter des ennemis et absorber leurs pouvoirs pour progresser.',
    tags: ['Java'],
    github: 'https://github.com/ErenVarli/JAV-501',
  },
  {
    name: 'JobBoard',
    school: 'Epitech',
    year: '1ʳᵉ année',
    color: 'blue',
    description:
      'Application web permettant aux entreprises de publier des offres d\'emploi et aux utilisateurs de candidater. Inclut une interface d\'administration pour gérer les annonces et une interface utilisateur dédiée.',
    github: 'https://github.com/ErenVarli/JobBoard',
  },
  {
    name: 'TrellTech',
    school: 'Epitech',
    year: '1ʳᵉ année',
    color: 'blue',
    description:
      'Application native développée rapidement, intégrant et exploitant les API de Trello pour améliorer la gestion de projet, organiser les tâches et faciliter la collaboration en équipe.',
    tags: ['App native', 'API Trello'],
    github: 'https://github.com/ErenVarli/TrellTech',
  },
  {
    name: 'Immobill',
    school: 'TYTD',
    year: '2ᵉ année',
    color: 'yellow',
    description:
      'Application web de gestion immobilière développée avec Symfony. Simplifie la gestion de biens, automatise les tâches locatives et offre une interface intuitive. Gestion des propriétés/propriétaires, authentification et contrôle d\'accès.',
    tags: ['Symfony', 'PHP'],
    github: 'https://github.com/ErenVarli/Immobill',
  },
  {
    name: 'musee-fa',
    school: 'TYTD',
    year: '1ʳᵉ année',
    color: 'yellow',
    description:
      'Application de gestion de réservations pour le site archéologique du Fâ. Simplification du processus de réservation, sécurisation des données via requêtes préparées et filtres, méthodologie Agile pour favoriser la collaboration. Améliore la gestion du flux de visiteurs et optimise la charge de travail du personnel.',
    tags: ['SQL', 'Agile'],
    github: 'https://github.com/ErenVarli/musee-fa',
  },
  {
    name: 'android-api-rest',
    school: 'TYTD',
    year: '1ʳᵉ année',
    color: 'yellow',
    description:
      'Application mobile Java exploitant des API REST pour gérer des données capturées avec une gestion efficace de la base de données. L\'un des projets réalisés durant ma formation Java.',
    tags: ['Java', 'Android', 'API REST'],
    github: 'https://github.com/ErenVarli/android-api-rest',
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

export default function SchoolProjects() {
  const [ref, inView] = useInView();
  const [open, setOpen] = useState(null);

  const toggle = (name) => setOpen(prev => (prev === name ? null : name));

  return (
    <section className="school section" id="school" ref={ref}>
      <p className="section__label">Projets d'études</p>
      <h2 className="section__title">
        Mes <span className="keyword">classiques</span> de l'école
      </h2>
      <div className={`school__table-wrap${inView ? ' is-visible' : ''}`}>
        <table className="school__table">
          <thead>
            <tr>
              <th scope="col">Projet</th>
              <th scope="col" className="school__col-school">École</th>
              <th scope="col" className="school__col-year">Année</th>
              <th scope="col" className="school__col-action">
                <span className="school__sr">Détails</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {schoolProjects.map((p) => {
              const isOpen = open === p.name;
              const detailId = `school-detail-${slug(p.name)}`;
              return (
                <Fragment key={p.name}>
                  <tr
                    className={`school__row${isOpen ? ' is-open' : ''}`}
                    onClick={() => toggle(p.name)}
                  >
                    <th scope="row" className="school__name">
                      <span className={`school__dot school__dot--${p.color}`} aria-hidden="true" />
                      {p.name}
                      <span className="school__meta-mobile">{p.school} · {p.year}</span>
                    </th>
                    <td className="school__col-school">{p.school}</td>
                    <td className="school__col-year">{p.year}</td>
                    <td className="school__col-action">
                      <button
                        type="button"
                        className="school__toggle"
                        aria-expanded={isOpen}
                        aria-controls={detailId}
                        aria-label={`${isOpen ? 'Masquer' : 'Afficher'} le détail de ${p.name}`}
                        onClick={(e) => { e.stopPropagation(); toggle(p.name); }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="school__detail-row" id={detailId}>
                      <td colSpan={4}>
                        <div className="school__detail">
                          <p className="school__desc">{p.description}</p>
                          {p.tags && (
                            <div className="school__tags">
                              {p.tags.map(t => (
                                <span key={t} className="skill-tag school__tag">{t}</span>
                              ))}
                            </div>
                          )}
                          {p.github && (
                            <a
                              href={p.github}
                              target="_blank"
                              rel="noreferrer"
                              className="school__link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Voir sur GitHub
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="7" y1="17" x2="17" y2="7" />
                                <polyline points="7 7 17 7 17 17" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
